import { useMemo } from "react";
import { usePartners } from "@/pages/partners/hooks";
import { usePartnerContacts } from "@/pages/partners/contacts/hooks";
import { useFilteredContracts } from "@/pages/contracts/hooks";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { TaskLink } from "@/entities/tasks/types";

/**
 * `tasks_entities_view` unions only deals, companies, leads and people, so the
 * name the API returns for a partner, partner contact or contract is a
 * placeholder rather than a real name — never trust it, always resolve here.
 */
export const useTaskLinkNames = (links: TaskLink[]): TaskLink[] => {
  const idsByType = useMemo(() => {
    const idsOf = (entityType: EntityType) =>
      links
        .filter((link) => link.entityType === entityType)
        .map((link) => link.entityId);
    return {
      partners: idsOf(EntityType.PARTNER),
      partnerContacts: idsOf(EntityType.PARTNER_CONTACT),
      contracts: idsOf(EntityType.CONTRACT)
    };
  }, [links]);

  const { entities: partners } = usePartners(idsByType.partners);
  const { entities: partnerContacts } = usePartnerContacts(
    idsByType.partnerContacts
  );
  const contractFilters = useMemo(
    () =>
      idsByType.contracts.length > 0
        ? { id: { in: idsByType.contracts } }
        : {},
    [idsByType.contracts]
  );
  const { contracts } = useFilteredContracts(
    undefined,
    undefined,
    contractFilters,
    "",
    idsByType.contracts.length > 0,
    true
  );

  const resolvedNames = useMemo(() => {
    const names = new Map<string, string>();
    partners?.forEach((partner) => {
      if (partner?.id && partner.name) names.set(partner.id, partner.name);
    });
    partnerContacts?.forEach((contact) => {
      if (contact?.id) names.set(contact.id, getPartnerContactFullName(contact));
    });
    contracts?.forEach((contract) => {
      if (contract?.id && contract.name) names.set(contract.id, contract.name);
    });
    return names;
  }, [partners, partnerContacts, contracts]);

  return useMemo(
    () =>
      links.map((link) => {
        const resolvedName = resolvedNames.get(link.entityId);
        return resolvedName ? { ...link, entityName: resolvedName } : link;
      }),
    [links, resolvedNames]
  );
};
